import type { ListenOptions, VoiceInput } from "./types.js";

/** Whisper works on 16 kHz mono; smaller uploads too. */
const RATE = 16_000;
/** End of utterance: this much silence after speech. */
const END_SILENCE_MS = 1200;
const FRAME = 4096;

export interface CloudSttTarget {
  serverUrl: string;
  token: string;
}

/** Root mean square of one frame, 0..1. */
export function rms(frame: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < frame.length; i++) sum += frame[i]! * frame[i]!;
  return Math.sqrt(sum / (frame.length || 1));
}

/** Linear resample to 16 kHz and encode as 16-bit PCM WAV. */
export function encodeWav(chunks: readonly Float32Array[], inputRate: number): Blob {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const ratio = inputRate / RATE;
  const outLen = Math.floor(total / ratio);
  const pcm = new Int16Array(outLen);
  let chunk = 0;
  let consumed = 0;
  for (let i = 0; i < outLen; i++) {
    const pos = Math.floor(i * ratio);
    while (chunk < chunks.length && pos >= consumed + chunks[chunk]!.length) {
      consumed += chunks[chunk]!.length;
      chunk++;
    }
    const s = chunks[chunk]?.[pos - consumed] ?? 0;
    pcm[i] = Math.max(-1, Math.min(1, s)) * 0x7fff;
  }
  const header = new DataView(new ArrayBuffer(44));
  const str = (o: number, t: string) => {
    for (let i = 0; i < t.length; i++) header.setUint8(o + i, t.charCodeAt(i));
  };
  str(0, "RIFF");
  header.setUint32(4, 36 + pcm.byteLength, true);
  str(8, "WAVE");
  str(12, "fmt ");
  header.setUint32(16, 16, true);
  header.setUint16(20, 1, true);
  header.setUint16(22, 1, true);
  header.setUint32(24, RATE, true);
  header.setUint32(28, RATE * 2, true);
  header.setUint16(32, 2, true);
  header.setUint16(34, 16, true);
  str(36, "data");
  header.setUint32(40, pcm.byteLength, true);
  return new Blob([header.buffer, pcm.buffer], { type: "audio/wav" });
}

/**
 * Speech to text with Whisper on the owner's own Cloudflare Worker (opt-in in Settings).
 * The utterance lives only in memory: it is sent once for transcription and dropped,
 * nothing is stored on the device or on the server.
 */
export class CloudVoiceInput implements VoiceInput {
  readonly kind = "cloud";
  private cancel: (() => void) | null = null;

  constructor(private readonly target: () => CloudSttTarget) {}

  async available(): Promise<boolean> {
    return typeof navigator.mediaDevices?.getUserMedia === "function" && typeof AudioContext === "function";
  }

  async listen(opts: ListenOptions): Promise<string | null> {
    this.cancel?.();
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
      });
    } catch {
      opts.onError?.("Brak zgody na mikrofon (Ustawienia Androida → Aplikacje → Squire).");
      return null;
    }
    const ctx = new AudioContext();
    const source = ctx.createMediaStreamSource(stream);
    // ScriptProcessor is deprecated but needs no extra module file; one node, removed after each listen.
    const node = ctx.createScriptProcessor(FRAME, 1, 1);
    const chunks: Float32Array[] = [];
    const maxFrames = Math.ceil((((opts.maxMs ?? 15_000) / 1000) * ctx.sampleRate) / FRAME);
    const recorded = await new Promise<boolean>((resolve) => {
      let noise = 0;
      let frames = 0;
      let speaking = false;
      let lastVoice = 0;
      const started = performance.now();
      const finish = (ok: boolean) => {
        node.onaudioprocess = null;
        this.cancel = null;
        resolve(ok);
      };
      this.cancel = () => finish(false);
      node.onaudioprocess = (e) => {
        const frame = new Float32Array(e.inputBuffer.getChannelData(0));
        const level = rms(frame);
        const now = performance.now();
        frames++;
        // First ~250 ms calibrate the noise floor; speech is clearly above it.
        if (frames <= 3) noise = Math.max(noise, level);
        const threshold = Math.max(0.015, noise * 2.5);
        if (level > threshold) {
          if (!speaking) opts.onPartial?.("…");
          speaking = true;
          lastVoice = now;
        }
        // Keep a little audio before speech starts so the first syllable is not cut.
        if (speaking || chunks.length < 2) chunks.push(frame);
        else chunks.splice(0, chunks.length, chunks[chunks.length - 1]!, frame);
        if (!speaking && now - started > opts.timeoutMs) finish(false);
        else if (speaking && now - lastVoice > END_SILENCE_MS) finish(true);
        else if (chunks.length >= maxFrames) finish(true);
      };
      source.connect(node);
      node.connect(ctx.destination);
    });
    source.disconnect();
    node.disconnect();
    for (const t of stream.getTracks()) t.stop();
    const rate = ctx.sampleRate;
    void ctx.close();
    if (!recorded) return null;
    const wav = encodeWav(chunks, rate);
    chunks.length = 0;
    opts.onPartial?.("Rozpoznaję…");
    return this.transcribe(wav, opts);
  }

  stop(): void {
    this.cancel?.();
  }

  async diagnose(): Promise<string> {
    const t = this.target();
    if (!t.serverUrl || !t.token) return "Rozpoznawanie przez Cloudflare: brak adresu serwera albo tokenu.";
    return "Rozpoznawanie mowy: Whisper na twoim Cloudflare (nagranie nie jest zapisywane).";
  }

  private async transcribe(wav: Blob, opts: ListenOptions): Promise<string | null> {
    const t = this.target();
    try {
      const res = await fetch(`${t.serverUrl.replace(/\/$/, "")}/api/stt`, {
        method: "POST",
        headers: { authorization: `Bearer ${t.token}`, "content-type": "audio/wav" },
        body: wav,
        signal: AbortSignal.timeout(20_000),
      });
      if (!res.ok) {
        opts.onError?.(
          res.status === 429 || res.status === 503
            ? "Dzienny limit rozpoznawania mowy na Cloudflare wyczerpany. Napisz albo spróbuj jutro."
            : `Rozpoznawanie mowy: błąd serwera (${res.status}).`,
        );
        return null;
      }
      const { text } = (await res.json()) as { text?: string };
      return text?.trim() || null;
    } catch {
      opts.onError?.("Rozpoznawanie mowy: brak połączenia z serwerem.");
      return null;
    }
  }
}
